import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight, MoveHorizontal } from "lucide-react";
import { FadeUp, RevealLinesOnView } from "../Reveal";
import { PRINTS, GIFTS, ALBUMS, IMG } from "../../lib/site";

const EASE = [0.16, 1, 0.3, 1];

function ProductCard({ p, i, testid }) {
  return (
    <FadeUp delay={(i % 3) * 0.08}>
      <div data-testid={testid} data-cursor="view" className="group cursor-pointer">
        <div className="relative aspect-[4/5] overflow-hidden border border-neutral-200 transition-all duration-500 group-hover:-translate-y-1.5 group-hover:border-pp-red group-hover:shadow-[0_25px_50px_-20px_rgba(0,0,0,0.3)]">
          <img src={p.image} alt={p.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
        </div>
        <div className="mt-4 flex items-start justify-between">
          <div>
            <h3 className="font-display text-xl font-bold text-pp-ink transition-colors group-hover:text-pp-red">{p.name}</h3>
            <p className="mt-1 text-sm text-neutral-500">{p.desc}</p>
          </div>
          <ArrowRight size={18} className="mt-1 shrink-0 text-pp-red transition-transform duration-300 group-hover:translate-x-1" />
        </div>
      </div>
    </FadeUp>
  );
}

function Prints() {
  return (
    <section id="prints" data-testid="prints-section" className="scroll-mt-24 bg-neutral-50 py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <p className="overline mb-5 text-pp-red">Photo Printing</p>
            <RevealLinesOnView lines={["PRINT YOUR", "MEMORIES."]} className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl" />
          </div>
          <p className="text-neutral-600 md:col-span-4">From everyday snapshots to large statement prints, we bring your photographs to life.</p>
        </div>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
          {PRINTS.map((p, i) => <ProductCard key={p.name} p={p} i={i} testid={`print-card-${i}`} />)}
        </div>
      </div>
    </section>
  );
}

function Gifts() {
  return (
    <section id="gifts" data-testid="gifts-section" className="scroll-mt-24 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-14 grid grid-cols-1 gap-6 md:grid-cols-12 md:items-end">
          <div className="md:col-span-8">
            <p className="overline mb-5 text-pp-red">Photo Gifts</p>
            <RevealLinesOnView lines={["MEMORIES MAKE", "THE BEST GIFTS."]} className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-7xl" />
          </div>
          <p className="text-neutral-600 md:col-span-4">Turn your favourite photographs into personalized gifts for the people who matter.</p>
        </div>
        <div className="grid grid-cols-2 gap-6 md:grid-cols-3">
          {GIFTS.map((p, i) => <ProductCard key={p.name} p={p} i={i} testid={`gift-card-${i}`} />)}
        </div>
      </div>
    </section>
  );
}

function Albums() {
  return (
    <section id="albums" data-testid="albums-section" className="scroll-mt-24 bg-pp-black noise py-24 text-white md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="mb-14">
          <p className="overline mb-5 text-pp-red">Photo Albums</p>
          <RevealLinesOnView lines={["MEMORIES,", "BOUND BEAUTIFULLY."]} className="font-display text-5xl font-black leading-[0.9] tracking-tight text-white md:text-7xl" />
        </div>
        <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
          {ALBUMS.map((a, i) => (
            <FadeUp key={a.name} delay={i * 0.1}>
              <div data-testid={`album-card-${i}`} data-cursor="view" className="group cursor-pointer">
                <div className="aspect-[4/3] overflow-hidden">
                  <img src={a.image} alt={a.name} loading="lazy" className="h-full w-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:scale-105" />
                </div>
                <h3 className="mt-4 font-display text-2xl font-bold transition-colors group-hover:text-pp-red">{a.name}</h3>
              </div>
            </FadeUp>
          ))}
        </div>
      </div>
    </section>
  );
}

function Restoration() {
  const [val, setVal] = useState(50);
  return (
    <section id="restoration" data-testid="restoration-section" className="scroll-mt-24 bg-white py-24 md:py-32">
      <div className="mx-auto max-w-[1500px] px-5 md:px-10">
        <div className="grid grid-cols-1 gap-12 md:grid-cols-12 md:items-center">
          <div className="md:col-span-5">
            <p className="overline mb-5 text-pp-red">Photo Restoration</p>
            <h2 className="font-display text-5xl font-black leading-[0.9] tracking-tight text-pp-ink md:text-6xl">
              BRING OLD MEMORIES<br />BACK TO LIFE.
            </h2>
            <p className="mt-6 max-w-md text-neutral-600">Drag the slider to see how we revive faded, damaged photographs — ready to print and frame again.</p>
            <div className="mt-8 flex items-center gap-3 text-sm text-neutral-500">
              <MoveHorizontal size={18} className="text-pp-red" /> Drag to compare
            </div>
          </div>

          <div className="md:col-span-6 md:col-start-7">
            <div className="relative aspect-[4/3] w-full select-none overflow-hidden bg-neutral-100" data-testid="restoration-slider-wrap">
              <img src={IMG.oldPhoto} alt="Old photo" className="absolute inset-0 h-full w-full object-cover grayscale" style={{ filter: "grayscale(1) contrast(0.8) brightness(0.9)" }} />
              <div className="absolute inset-0 overflow-hidden" style={{ width: `${val}%` }}>
                <img src={IMG.oldPhoto} alt="Restored photo" className="h-full w-full object-cover" style={{ width: "100%", filter: "saturate(1.25) contrast(1.1) brightness(1.08) sepia(0.15)", maxWidth: "none" }} />
              </div>
              <div className="pointer-events-none absolute inset-y-0 z-10 w-0.5 bg-pp-red" style={{ left: `${val}%` }}>
                <div className="absolute top-1/2 flex h-9 w-9 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full border-2 border-pp-red bg-white">
                  <MoveHorizontal size={16} className="text-pp-red" />
                </div>
              </div>
              <span className="absolute bottom-3 left-3 z-10 bg-pp-red px-2 py-1 overline text-white">Restored</span>
              <span className="absolute bottom-3 right-3 z-10 bg-black/70 px-2 py-1 overline text-white">Original</span>
              <input
                type="range"
                min="0"
                max="100"
                value={val}
                onChange={(e) => setVal(Number(e.target.value))}
                data-testid="restoration-slider"
                className="absolute inset-0 z-20 h-full w-full cursor-ew-resize opacity-0"
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Store() {
  return (
    <>
      <Prints />
      <Gifts />
      <Albums />
      <Restoration />
    </>
  );
}
